import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { PacModeBanner } from "@/components/ops/PacModeBanner";
import {
  FileText, Search, Plus, Trash2, RefreshCw, CheckCircle2,
  ChevronRight, ChevronLeft, Info, User, Package, CreditCard,
} from "lucide-react";

const STEPS = ["Receptor", "Conceptos", "Pago", "Revision"];

const FORMAS_PAGO_COMUNES = [
  { clave: "01", descripcion: "Efectivo" },
  { clave: "03", descripcion: "Transferencia electronica" },
  { clave: "04", descripcion: "Tarjeta de credito" },
  { clave: "28", descripcion: "Tarjeta de debito" },
];

const METODOS_PAGO = [
  { clave: "PUE", descripcion: "Pago en una sola exhibicion" },
  { clave: "PPD", descripcion: "Pago en parcialidades o diferido" },
];

const EMPTY_CONCEPTO = {
  clave_prod_serv: "90101501",
  no_identificacion: "",
  cantidad: 1,
  clave_unidad: "E48",
  unidad: "Unidad de servicio",
  descripcion: "",
  valor_unitario: "",
  objeto_impuesto: "02",
  iva_rate: 16,
};

function ConceptoRow({ c, idx, onChange, onDelete, usosSearch }) {
  const importe = parseFloat(c.cantidad || 0) * parseFloat(c.valor_unitario || 0);
  const iva = c.objeto_impuesto === "02" ? round2(importe * (c.iva_rate / 100)) : 0;

  return (
    <div className="border border-slate-200 rounded-xl p-4 space-y-3 relative">
      <button onClick={() => onDelete(idx)} className="absolute top-3 right-3 text-slate-300 hover:text-red-500">
        <Trash2 className="w-4 h-4" />
      </button>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="md:col-span-2">
          <label className="text-xs text-slate-600 mb-1 block">Descripcion</label>
          <input
            data-testid={`concepto-desc-${idx}`}
            value={c.descripcion}
            onChange={e => onChange(idx, "descripcion", e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Ej: Pizza Margherita"
          />
        </div>
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Clave SAT</label>
          <input
            data-testid={`concepto-clave-${idx}`}
            value={c.clave_prod_serv}
            onChange={e => onChange(idx, "clave_prod_serv", e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
            placeholder="90101501"
            maxLength={8}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Cantidad</label>
          <input
            data-testid={`concepto-cant-${idx}`}
            type="number"
            min="0.001"
            step="0.001"
            value={c.cantidad}
            onChange={e => onChange(idx, "cantidad", e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Precio unitario</label>
          <input
            data-testid={`concepto-precio-${idx}`}
            type="number"
            min="0"
            step="0.01"
            value={c.valor_unitario}
            onChange={e => onChange(idx, "valor_unitario", e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Obj. Imp.</label>
          <select
            value={c.objeto_impuesto}
            onChange={e => onChange(idx, "objeto_impuesto", e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-2 py-2 text-xs"
          >
            <option value="01">01 No aplica</option>
            <option value="02">02 Con IVA</option>
            <option value="03">03 No desglose</option>
          </select>
        </div>
        {c.objeto_impuesto === "02" && (
          <div>
            <label className="text-xs text-slate-600 mb-1 block">IVA %</label>
            <select
              value={c.iva_rate}
              onChange={e => onChange(idx, "iva_rate", Number(e.target.value))}
              className="w-full border border-slate-300 rounded-lg px-2 py-2 text-xs"
            >
              <option value={16}>16%</option>
              <option value={8}>8%</option>
              <option value={0}>0%</option>
            </select>
          </div>
        )}
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Importe c/IVA</label>
          <p className="text-sm font-semibold text-slate-800 pt-2">
            ${(importe + iva).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>
    </div>
  );
}

function round2(n) { return Math.round(n * 100) / 100; }

export default function OpsFiscalNuevaFactura() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [entities, setEntities] = useState([]);
  const [usosCfdi, setUsosCfdi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [legalEntityId, setLegalEntityId] = useState("");
  const [receiver, setReceiver] = useState({
    rfc: "", nombre: "", uso_cfdi: "G03",
    domicilio_fiscal: "64000", regimen_fiscal: "612",
  });
  const [conceptos, setConceptos] = useState([{ ...EMPTY_CONCEPTO }]);
  const [pago, setPago] = useState({
    internal_serie: "A",
    forma_pago: "03",
    metodo_pago: "PUE",
    lugar_expedicion: "64000",
  });

  const [rfcLookup, setRfcLookup] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/ops/legal-entities"),
      api.get("/ops/fiscal/sat/usos-cfdi"),
    ]).then(([entRes, usosRes]) => {
      const ents = entRes.data;
      const arr = Array.isArray(ents) ? ents : ents.entities || ents.legal_entities || [];
      setEntities(arr);
      if (arr.length > 0) setLegalEntityId(arr[0].id);
      setUsosCfdi(usosRes.data.results || []);
    }).catch(() => toast.error("Error al cargar datos iniciales"));
  }, []);

  const handleConceptoChange = (idx, field, val) => {
    setConceptos(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
  };

  const addConcepto = () => setConceptos(prev => [...prev, { ...EMPTY_CONCEPTO }]);
  const deleteConcepto = (idx) => setConceptos(prev => prev.filter((_, i) => i !== idx));

  // Totales en tiempo real
  const totals = conceptos.reduce((acc, c) => {
    const imp = round2(parseFloat(c.cantidad || 0) * parseFloat(c.valor_unitario || 0));
    const iva = c.objeto_impuesto === "02" ? round2(imp * (c.iva_rate / 100)) : 0;
    return { subtotal: round2(acc.subtotal + imp), iva: round2(acc.iva + iva) };
  }, { subtotal: 0, iva: 0 });
  const totalFinal = round2(totals.subtotal + totals.iva);

  const buildPayload = () => {
    const conceptosPayload = conceptos.map(c => {
      const importe = round2(parseFloat(c.cantidad) * parseFloat(c.valor_unitario));
      const traslados = c.objeto_impuesto === "02" && c.iva_rate > 0
        ? [{ base: importe, impuesto: "002", tipo_factor: "Tasa", tasa_o_cuota: c.iva_rate / 100, importe: round2(importe * c.iva_rate / 100) }]
        : [];
      return {
        clave_prod_serv: c.clave_prod_serv,
        no_identificacion: c.no_identificacion || undefined,
        cantidad: parseFloat(c.cantidad),
        clave_unidad: c.clave_unidad,
        unidad: c.unidad,
        descripcion: c.descripcion,
        valor_unitario: parseFloat(c.valor_unitario),
        importe,
        objeto_impuesto: c.objeto_impuesto,
        impuestos: { traslados, retenciones: [] },
      };
    });
    return {
      legal_entity_id: legalEntityId,
      receiver,
      internal_serie: pago.internal_serie,
      tipo_comprobante: "I",
      forma_pago: pago.forma_pago,
      metodo_pago: pago.metodo_pago,
      moneda: "MXN",
      tipo_cambio: 1.0,
      lugar_expedicion: pago.lugar_expedicion,
      conceptos: conceptosPayload,
    };
  };

  const handleEmit = async () => {
    setEmitting(true);
    try {
      const res = await api.post("/ops/fiscal/invoices/emit", buildPayload());
      setResult(res.data);
      toast.success(
        res.data.is_demo
          ? `Factura de PRUEBA generada (sin validez fiscal): ${res.data.folio}`
          : `Factura timbrada: ${res.data.uuid_fiscal}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al timbrar factura");
    } finally {
      setEmitting(false);
    }
  };

  if (result) {
    const isDemo = !!result.is_demo;
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className={`bg-white rounded-2xl border p-8 text-center shadow-sm ${isDemo ? "border-amber-300" : "border-green-200"}`}>
          <CheckCircle2 className={`w-14 h-14 mx-auto mb-4 ${isDemo ? "text-amber-500" : "text-green-500"}`} />
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {isDemo ? "Factura de prueba generada" : "Factura timbrada correctamente"}
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            {isDemo
              ? "MODO PRUEBAS — este CFDI NO tiene validez fiscal y no entra en DIOT ni en el IVA mensual."
              : "El CFDI fue sellado y timbrado exitosamente por el PAC"}
          </p>
          {isDemo && (
            <div data-testid="emit-demo-warning" className="border-2 border-amber-400 bg-amber-50 rounded-xl p-3 mb-5 text-left">
              <p className="text-xs font-bold text-amber-900 uppercase">Sin validez fiscal</p>
              <p className="text-xs text-amber-800 mt-1">
                El UUID fue generado por un PAC simulado. Para emitir facturas reales hay que activar
                el modo PAC real con las credenciales del proveedor autorizado.
              </p>
            </div>
          )}
          <div className="bg-slate-50 rounded-xl p-4 text-left space-y-2 mb-6">
            {[
              ["Folio", result.folio],
              ["UUID Fiscal", <span className="font-mono text-xs">{result.uuid_fiscal}</span>],
              ["Total", `$${(result.total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN`],
              ["Fecha timbrado", result.fecha_timbrado],
              ["Estado", isDemo
                ? <span className="text-amber-700 font-medium">Timbrado (demo, sin validez fiscal)</span>
                : <span className="text-green-700 font-medium">Timbrado</span>],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <span className="text-slate-500">{k}</span>
                <span className="font-medium text-slate-800">{v}</span>
              </div>
            ))}
          </div>
          {result.xml_url && (
            <a href={result.xml_url} target="_blank" rel="noreferrer"
              className="block mb-3 bg-slate-800 hover:bg-slate-900 text-white rounded-lg px-4 py-2.5 text-sm font-medium">
              Descargar XML timbrado
            </a>
          )}
          <div className="flex gap-2">
            <button
              data-testid="btn-emitir-otra"
              onClick={() => { setResult(null); setStep(0); setConceptos([{ ...EMPTY_CONCEPTO }]); }}
              className="flex-1 border border-slate-300 rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
            >
              Emitir otra factura
            </button>
            <button
              onClick={() => navigate("/ops/fiscal/facturacion")}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium"
            >
              Ver listado
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <div className="mb-6"><PacModeBanner /></div>
      {/* Wizard header */}
      <div className="mb-8">
        <button onClick={() => navigate("/ops/fiscal/facturacion")} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-4">
          <ChevronLeft className="w-4 h-4" />
          Volver al listado
        </button>
        <h2 className="text-xl font-bold text-slate-900">Nueva factura CFDI 4.0</h2>
        <div className="flex items-center gap-2 mt-4">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                i === step ? "bg-blue-600 border-blue-600 text-white" :
                i < step ? "bg-green-500 border-green-500 text-white" :
                "bg-white border-slate-300 text-slate-400"
              }`}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs font-medium ${i === step ? "text-blue-700" : "text-slate-400"}`}>{s}</span>
              {i < STEPS.length - 1 && <div className="w-8 h-px bg-slate-200" />}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">

        {/* STEP 0: Receptor */}
        {step === 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-blue-600" />
              <h3 className="font-semibold text-slate-800 text-sm">Datos del receptor</h3>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Entidad legal emisora</label>
              <select
                data-testid="select-entity"
                value={legalEntityId}
                onChange={e => setLegalEntityId(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                {entities.map(e => (
                  <option key={e.id} value={e.id}>{e.legal_name} ({e.rfc})</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">RFC del receptor</label>
                <input
                  data-testid="input-rfc-receptor"
                  value={receiver.rfc}
                  onChange={e => {
                    const rfc = e.target.value.toUpperCase();
                    setReceiver(r => ({
                      ...r,
                      rfc,
                      ...(rfc === "XAXX010101000" ? { regimen_fiscal: "616", uso_cfdi: "S01", nombre: "PUBLICO EN GENERAL" } : {})
                    }));
                  }}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono uppercase"
                  placeholder="HEMJ900101ABC o XAXX010101000"
                  maxLength={13}
                />
                {receiver.rfc === "XAXX010101000" && (
                  <p className="text-xs text-amber-600 mt-1">RFC generico — publico en general. Se fuerza regimen 616 y uso S01.</p>
                )}
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Nombre / Razon social</label>
                <input
                  data-testid="input-nombre-receptor"
                  value={receiver.nombre}
                  onChange={e => setReceiver(r => ({ ...r, nombre: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Nombre completo del receptor"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Uso del CFDI</label>
                <select
                  data-testid="select-uso-cfdi"
                  value={receiver.uso_cfdi}
                  onChange={e => setReceiver(r => ({ ...r, uso_cfdi: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                >
                  {usosCfdi.map(u => (
                    <option key={u.clave} value={u.clave}>{u.clave} — {u.descripcion}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">CP domicilio fiscal receptor</label>
                <input
                  data-testid="input-cp-receptor"
                  value={receiver.domicilio_fiscal}
                  onChange={e => setReceiver(r => ({ ...r, domicilio_fiscal: e.target.value }))}
                  maxLength={5}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                  placeholder="64000"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Regimen fiscal receptor</label>
                <input
                  data-testid="input-regimen-receptor"
                  value={receiver.regimen_fiscal}
                  onChange={e => setReceiver(r => ({ ...r, regimen_fiscal: e.target.value }))}
                  maxLength={3}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                  placeholder="612"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 1: Conceptos */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-slate-800 text-sm">Conceptos</h3>
              </div>
              <button onClick={addConcepto} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                <Plus className="w-3 h-3" />
                Agregar concepto
              </button>
            </div>
            <div className="space-y-3">
              {conceptos.map((c, i) => (
                <ConceptoRow
                  key={i}
                  c={c}
                  idx={i}
                  onChange={handleConceptoChange}
                  onDelete={deleteConcepto}
                />
              ))}
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-right space-y-1">
              <p className="text-xs text-slate-500">Subtotal: <span className="font-mono">${totals.subtotal.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></p>
              <p className="text-xs text-slate-500">IVA: <span className="font-mono">${totals.iva.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></p>
              <p className="text-sm font-bold text-slate-900">Total: <span className="font-mono">${totalFinal.toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN</span></p>
            </div>
          </div>
        )}

        {/* STEP 2: Pago */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4 text-blue-600" />
              <h3 className="font-semibold text-slate-800 text-sm">Forma y metodo de pago</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Forma de pago</label>
                <select
                  data-testid="select-forma-pago"
                  value={pago.forma_pago}
                  onChange={e => setPago(p => ({ ...p, forma_pago: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                >
                  {FORMAS_PAGO_COMUNES.map(f => (
                    <option key={f.clave} value={f.clave}>{f.clave} — {f.descripcion}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Metodo de pago</label>
                <select
                  data-testid="select-metodo-pago"
                  value={pago.metodo_pago}
                  onChange={e => setPago(p => ({ ...p, metodo_pago: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                >
                  {METODOS_PAGO.map(m => (
                    <option key={m.clave} value={m.clave}>{m.clave} — {m.descripcion}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Serie de factura</label>
                <input
                  data-testid="input-serie"
                  value={pago.internal_serie}
                  onChange={e => setPago(p => ({ ...p, internal_serie: e.target.value.toUpperCase() }))}
                  maxLength={10}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono uppercase"
                  placeholder="A"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">CP lugar expedicion</label>
                <input
                  data-testid="input-lugar-expedicion"
                  value={pago.lugar_expedicion}
                  onChange={e => setPago(p => ({ ...p, lugar_expedicion: e.target.value }))}
                  maxLength={5}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                  placeholder="64000"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Revision */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              Revision antes de timbrar
            </h3>
            <div className="space-y-2 text-sm">
              {[
                ["Emisor", entities.find(e => e.id === legalEntityId)?.legal_name],
                ["Receptor", `${receiver.rfc} — ${receiver.nombre}`],
                ["Uso CFDI", receiver.uso_cfdi],
                ["Forma pago", pago.forma_pago],
                ["Metodo pago", pago.metodo_pago],
                ["Conceptos", `${conceptos.length} concepto(s)`],
                ["Subtotal", `$${totals.subtotal.toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN`],
                ["IVA total", `$${totals.iva.toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN`],
                ["TOTAL A PAGAR", <strong>${totalFinal.toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN</strong>],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-medium text-slate-800">{v}</span>
                </div>
              ))}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
              <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                Al hacer clic en <strong>Timbrar</strong>, el CFDI se firmara con el CSD activo de la entidad
                y se enviara al PAC para su timbrado. Esta accion no se puede deshacer sin cancelar el CFDI.
              </p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t border-slate-100">
          <button
            onClick={() => setStep(s => s - 1)}
            disabled={step === 0}
            className="border border-slate-300 rounded-lg px-4 py-2 text-sm text-slate-600 disabled:opacity-30 flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </button>
          {step < STEPS.length - 1 ? (
            <button
              data-testid={`btn-siguiente-paso-${step}`}
              onClick={() => setStep(s => s + 1)}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-1"
            >
              Siguiente
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="btn-timbrar-cfdi"
              onClick={handleEmit}
              disabled={emitting}
              className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-5 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {emitting && <RefreshCw className="w-4 h-4 animate-spin" />}
              {emitting ? "Timbrando..." : "Timbrar CFDI"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
